import React, { useCallback, useEffect, useState } from 'react';
import withTranslation from 'hoc/withTranslation';
import size from 'lodash/size';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import { useDispatch } from 'react-redux';
import { tourToRoutingRequest } from 'utils/RoutingConverter';
import styled from '@emotion/styled';
import { formatRuntime } from '../../../utils/timeFormatHelpers';
import {
  StyledWizardContent,
  StyledWizardH1,
  StyledFullWidthContainer,
  StyledText,
  StyledRuntimeTag,
  StyledChevronButton,
  StyledChevronIcon,
} from '../Global/WizardStyled';
import Tours from './Tours';
import Section from '../../Form/Section';
import { AMPLITUDE_EVENTS, AmplitudeService } from '../../../utils/amplitude';
import {
  getLastValidIteration,
  getTotalJobsInTours,
  getToursDataFromIteration,
} from '../../../utils/SolutionHelpers';
import SummaryWithDownload from './SummaryWithDownload';
import UnassignedList from './UnassignedList';
import { getRoutingTourInfo, selectUnassigned } from '../../../actions';
import { getSafeValue, setSafeValue } from '../../../utils/security';
import SimpleButton from '../../Form/SimpleButton';
import iconSearch from '../../../global/img/search.svg';
import { StyledActions, StyledTourDispatcherContainer } from '../Wizard';

const StyledDetailsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ withMargin }) => (withMargin ? '1.2rem' : '0')};
`;

const Step3Container = ({
  user,
  oAuth,
  tourData,
  onTourChange,
  selectedTours,
  sharedTours,
  solution,
  orders,
  tourPlanner,
  display,
  onSearchId,
  translations: {
    wizard: {
      tours: { toursTitle, noTours, detailsTitleTrans, runTimeTrans },
    },
  },
}) => {
  const dispatch = useDispatch();
  const runtime = solution.requests[solution.show].runtime;
  const [showDetails, setShowDetails] = useState(true);
  const handleSelectUnassigned = useCallback(
    (show) => dispatch(selectUnassigned(show)),
    [dispatch],
  );
  const solReq = solution.requests[solution.show];
  const notices = solReq?.notices;
  const unassigned = solReq?.unassigned;
  const showUnassigned = tourData?.tours.length === 0 || (notices && unassigned);
  const tempOrders = solution.requests[solution.show].tempOrders;
  const hideRoutes = user.mapSettings.hideAllRoutes;

  const handleGetRoutingTourInfo = useCallback(
    (tour) =>
      dispatch(
        getRoutingTourInfo({
          oAuth,
          routingRequest: tourToRoutingRequest(
            tour,
            tourPlanner,
            tempOrders || orders,
            null,
            hideRoutes,
          ),
          tourId: 0,
          solutionId: solution.show,
        }),
      ),
    [dispatch, oAuth, tourPlanner, solution.show, orders, tempOrders, hideRoutes],
  );

  useEffect(() => {
    if (!tourData) return;
    const slnInfo = {
      assignedOrders: getTotalJobsInTours(tourData.tours),
      unassignedOrders: size(tourData.unassigned),
      totalTours: size(tourData.tours),
      totalDuration: tourData.statistic.duration,
      totalDistance: tourData.statistic.distance,
      totalCost: tourData.statistic.cost,
    };

    if (!isEmpty(tourData.unassigned)) {
      const grouped = {};
      tourData.unassigned.forEach((item) => {
        item.reasons.forEach((reason) => {
          if (!getSafeValue(grouped, reason.code)) setSafeValue(grouped, reason.code, []);
          getSafeValue(grouped, reason.code).push(item);
        });
      });
      keys(grouped).forEach((key) => {
        setSafeValue(slnInfo, `unassignedOrdersCode${key}`, size(getSafeValue(grouped, key)));
      });
    }

    AmplitudeService.log(AMPLITUDE_EVENTS.TOUR_OVERVIEW, slnInfo);
  }, [tourData]);

  const handleOnChangeView = useCallback((viewTours) => {
    handleSelectUnassigned(!viewTours);
  });

  const preventTextSelection = (event) => {
    if (event.shiftKey) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    const filtered = getLastValidIteration(solution.requests[solution.show]);
    const tours = getToursDataFromIteration(filtered)?.tours;

    if (!showUnassigned && tours) {
      if (tours.length === 0) handleSelectUnassigned();
      else handleGetRoutingTourInfo(tours[0]);
    }
  }, [oAuth?.accessToken, showUnassigned]);

  return (
    <StyledWizardContent sticky data-test-id="tourOverviewPanel">
      <StyledDetailsHeader withMargin>
        <StyledWizardH1 noMargin>{toursTitle}</StyledWizardH1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <StyledRuntimeTag title={`${runTimeTrans} ${formatRuntime(runtime)}`}>
            ⏱ <strong>{formatRuntime(runtime)}</strong>
          </StyledRuntimeTag>
          <StyledTourDispatcherContainer searchOnly static>
            <StyledActions searchOnly>
              <SimpleButton
                id="button-search-Job"
                icon={iconSearch}
                onClick={() => onSearchId()}
                smaller
                noMargin
              />
            </StyledActions>
          </StyledTourDispatcherContainer>
        </div>
      </StyledDetailsHeader>
      {tourData.tours.length > 0 && (
        <>
          <Section>
            <StyledDetailsHeader>
              <StyledText detailsTitle>{detailsTitleTrans}</StyledText>
              <StyledChevronButton onClick={() => setShowDetails((v) => !v)}>
                <StyledChevronIcon open={showDetails} />
              </StyledChevronButton>
            </StyledDetailsHeader>
            {showDetails && (
              <SummaryWithDownload
                lastIterationTourData={tourData}
                user={user}
                tourPlanner={tourPlanner}
                orders={orders}
                solution={solution}
                isToursView={!display.showUnassigned}
                onChangeView={handleOnChangeView}
              />
            )}
          </Section>
          {!display.showUnassigned ? (
            <StyledFullWidthContainer
              sticky
              onMouseDown={preventTextSelection}
              onMouseUp={preventTextSelection}
            >
              <Tours
                id="solutionTours"
                oAuth={oAuth}
                lastIterationTourData={tourData}
                onTourChange={onTourChange}
                selectedTours={selectedTours}
                sharedTours={sharedTours}
                solution={solution}
                user={user}
                tourPlanner={tourPlanner}
                display={display}
                detailsVisible={showDetails}
              />
            </StyledFullWidthContainer>
          ) : (
            <UnassignedList
              lastIterationTourData={tourData}
              orders={orders}
              oAuth={oAuth}
              tourPlanner={tourPlanner}
              solution={solution}
            />
          )}
        </>
      )}
      {showUnassigned && (
        <>
          <Section>
            <StyledText>{noTours}</StyledText>
          </Section>
          <Section>
            <UnassignedList
              lastIterationTourData={tourData}
              orders={orders}
              oAuth={oAuth}
              tourPlanner={tourPlanner}
              solution={solution}
              solNotices={notices}
              solUnassigned={unassigned}
              noTours
            />
          </Section>
        </>
      )}
    </StyledWizardContent>
  );
};

export default withTranslation(Step3Container);
